import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Layers,
  HardHat,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { formatMoney } from '@vns/core';
import { useLookups, PAYMENT_MODES } from '../lib/lookups';
import { useSubcontractorContracts, useCreateSubcontractor } from '../lib/directory';
import {
  useSubcontractorBalanceSummary,
  useRecordSubcontractorLabour,
  useUpdateSubcontractorLabour,
  cleanWorkTypeName,
  cleanDisplayRemarks,
  type SubcontractorLabourDraft,
  type SubcontractorWorkLineDraft,
  type SubcontractorIndividualWorkLineDraft,
  type SubcontractorTxRow,
} from '../lib/transactions';
import { Drawer, Field, Select, useToast } from './ui';

const INDIVIDUAL_UNITS = [
  { value: 'Sq.ft', label: 'Sq.ft (Square Feet)' },
  { value: 'CFT', label: 'CFT (Cubic Feet)' },
  { value: 'RFT', label: 'RFT (Running Feet)' },
  { value: 'KG', label: 'KG (Kilogram)' },
  { value: 'No', label: 'No (Numbers / Pieces)' },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface SubcontractorTransactionDrawerProps {
  projectId?: string;
  existing?: SubcontractorTxRow | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function SubcontractorTransactionDrawer({
  projectId: defaultProjectId,
  existing,
  onClose,
  onSuccess,
}: SubcontractorTransactionDrawerProps) {
  const { data: lookups } = useLookups();
  const queryClient = useQueryClient();
  const toast = useToast();

  const recordSubcontractorLabour = useRecordSubcontractorLabour();
  const updateSubcontractorLabour = useUpdateSubcontractorLabour();
  const createSubcontractor = useCreateSubcontractor();
  const { data: allSubcontractorContracts } = useSubcontractorContracts();

  const isSaving = recordSubcontractorLabour.isPending || updateSubcontractorLabour.isPending;

  // Project map for quick client & name lookup
  const projectMap = useMemo(() => {
    const map = new Map<string, { id: string; name: string; value_paise?: number; client_id?: string }>();
    lookups?.projects?.forEach((p) => map.set(p.id, p));
    return map;
  }, [lookups?.projects]);

  const createInitialSubcontractorDraft = (mode: 'weekly' | 'individual' = 'weekly'): SubcontractorLabourDraft => {
    const proj = defaultProjectId ? projectMap.get(defaultProjectId) : undefined;
    return {
      date: todayIso(),
      clientId: proj?.client_id || '',
      projectId: defaultProjectId || '',
      subcontractorId: '',
      txType: 'debit',
      entryMode: mode,
      nameOfWork: '',
      workVolume: '',
      unit: 'Sq.ft',
      rate: '',
      creditAmountRupees: '',
      lines: [
        {
          work_type_id: '',
          worker_count: '1',
          daily_rate: '',
          totalAmountPaise: 0,
          remarks: '',
          custom_work_name: '',
          is_custom: false,
        },
      ],
      individualLines: [
        {
          nameOfWork: '',
          workVolume: '',
          unit: 'Sq.ft',
          rate: '',
        },
      ],
      paidAmountRupees: '',
      mode: 'Cash',
      remarks: '',
    };
  };

  const [subcontractorDraft, setSubcontractorDraft] = useState<SubcontractorLabourDraft>(() =>
    createInitialSubcontractorDraft('weekly'),
  );

  // Quick Add Subcontractor (for individual mode)
  const [showQuickAddSub, setShowQuickAddSub] = useState(false);
  const [newSubName, setNewSubName] = useState('');
  const [newSubPhone, setNewSubPhone] = useState('');
  const [newSubLocation, setNewSubLocation] = useState('');
  const [isCreatingSub, setIsCreatingSub] = useState(false);

  const handleCreateQuickSub = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    if (!newSubName.trim()) {
      toast.show('Please enter subcontractor name', true);
      return;
    }
    if (!newSubPhone.trim()) {
      toast.show('Please enter phone number', true);
      return;
    }
    try {
      setIsCreatingSub(true);
      const created = await createSubcontractor.mutateAsync({
        name: newSubName.trim(),
        phone: newSubPhone.trim(),
        city: newSubLocation.trim() || undefined,
        address: newSubLocation.trim() || undefined,
        workTypeIds: [],
      });
      setSubcontractorDraft((prev) => ({ ...prev, subcontractorId: created.id }));
      setNewSubName('');
      setNewSubPhone('');
      setNewSubLocation('');
      setShowQuickAddSub(false);
      toast.show('Sub contractor added successfully');
    } catch (err: any) {
      toast.show(err.message || 'Failed to add subcontractor', true);
    } finally {
      setIsCreatingSub(false);
    }
  };

  // Sync draft when editing or when defaultProjectId changes
  useEffect(() => {
    if (existing) {
      const isDebit = existing.overallTotalPaise === 0 && existing.paidAmountPaise > 0;
      const indMatch = (existing.remarks || existing.lines[0]?.remarks || '').match(
        /\[Individual:\s*([\d.]+)\s*([\w.]+)\s*@\s*₹?([\d.]+)\/([^\]\s]+)\](?:\s*(.*))?/,
      );
      const isIndividual = Boolean(indMatch || (existing.remarks || '').includes('[Individual'));
      const proj = projectMap.get(existing.projectId);

      const individualLines: SubcontractorIndividualWorkLineDraft[] = [];
      if (isIndividual && existing.lines.length > 0) {
        existing.lines.forEach((l) => {
          const lineIndMatch = (l.remarks || existing.remarks || '').match(
            /\[Individual:\s*([\d.]+)\s*([\w.]+)\s*@\s*₹?([\d.]+)\/([^\]\s]+)\](?:\s*(.*))?/,
          );
          if (lineIndMatch) {
            individualLines.push({
              nameOfWork: l.workTypeName || '',
              workVolume: lineIndMatch[1] || '',
              unit: lineIndMatch[2] || 'Sq.ft',
              rate: lineIndMatch[3] || '',
            });
          } else {
            individualLines.push({
              nameOfWork: l.workTypeName || '',
              workVolume: '1',
              unit: 'Sq.ft',
              rate: l.totalAmountPaise > 0 ? (l.totalAmountPaise / 100).toString() : '',
            });
          }
        });
      }
      if (individualLines.length === 0) {
        individualLines.push({
          nameOfWork: isIndividual ? existing.lines[0]?.workTypeName || '' : '',
          workVolume: isIndividual && indMatch ? indMatch[1] || '' : '',
          unit: isIndividual && indMatch ? indMatch[2] || 'Sq.ft' : 'Sq.ft',
          rate: isIndividual && indMatch ? indMatch[3] || '' : '',
        });
      }

      const lines: SubcontractorWorkLineDraft[] = existing.lines.length
        ? existing.lines.map((l) => {
            const wt = lookups?.workTypes?.find((w) => w.name === l.workTypeName);
            return {
              work_type_id: wt?.id || (l.workTypeName ? '__other__' : ''),
              worker_count: '1',
              daily_rate:
                l.totalAmountPaise > 0
                  ? (l.totalAmountPaise / 100).toString()
                  : l.ratePerWorkerPaise > 0
                  ? (l.ratePerWorkerPaise / 100).toString()
                  : '',
              totalAmountPaise: l.totalAmountPaise,
              remarks: l.remarks || '',
              custom_work_name: !wt && l.workTypeName ? l.workTypeName : '',
              is_custom: !wt && Boolean(l.workTypeName),
            };
          })
        : [
            {
              work_type_id: '',
              worker_count: '1',
              daily_rate: '',
              totalAmountPaise: 0,
              remarks: '',
              custom_work_name: '',
              is_custom: false,
            },
          ];

      setSubcontractorDraft({
        date: existing.date,
        clientId: proj?.client_id || '',
        projectId: existing.projectId,
        subcontractorId: existing.subcontractorId,
        txType: isDebit ? 'debit' : 'credit',
        entryMode: isIndividual ? 'individual' : 'weekly',
        nameOfWork: individualLines[0]?.nameOfWork || '',
        workVolume: individualLines[0]?.workVolume || '',
        unit: individualLines[0]?.unit || 'Sq.ft',
        rate: individualLines[0]?.rate || '',
        creditAmountRupees: !isDebit && existing.overallTotalPaise > 0 ? (existing.overallTotalPaise / 100).toString() : '',
        lines,
        individualLines,
        paidAmountRupees: existing.paidAmountPaise > 0 ? (existing.paidAmountPaise / 100).toString() : '',
        mode: 'Cash',
        remarks: isIndividual && indMatch && indMatch[5] ? indMatch[5] : existing.remarks || '',
      });
    } else if (defaultProjectId) {
      const proj = projectMap.get(defaultProjectId);
      setSubcontractorDraft((prev) => ({
        ...prev,
        projectId: defaultProjectId,
        clientId: proj?.client_id || prev.clientId,
      }));
    }
  }, [existing, defaultProjectId, projectMap, lookups?.workTypes]);

  // Subcontractor projects filtered by client
  const subcontractorProjects = useMemo(() => {
    if (!subcontractorDraft.clientId) return lookups?.projects ?? [];
    return (lookups?.projects ?? []).filter((p) => p.client_id === subcontractorDraft.clientId);
  }, [lookups?.projects, subcontractorDraft.clientId]);

  const subcontractorWorkTypeOptions = useMemo(() => {
    const list = (lookups?.workTypes ?? [])
      .filter((w) => !/\s*\((?:Visit|Entry|Bill)\s*\d+\)/i.test(w.name))
      .map((w) => ({ value: w.id, label: cleanWorkTypeName(w.name) }));
    const hasOther = list.some((w) => w.label.trim().toLowerCase() === 'others' || w.label.trim().toLowerCase() === 'other');
    if (!hasOther) {
      list.push({ value: '__other__' as string, label: 'Others' });
    }
    return list;
  }, [lookups?.workTypes]);

  const handleSubcontractorClientChange = (clientId: string) => {
    setSubcontractorDraft((prev) => {
      const currentProj = lookups?.projects?.find((p) => p.id === prev.projectId);
      const resetProject = clientId && currentProj && currentProj.client_id !== clientId;
      return {
        ...prev,
        clientId,
        projectId: resetProject ? '' : prev.projectId,
      };
    });
  };

  const handleSubcontractorProjectChange = (projectId: string) => {
    const proj = lookups?.projects?.find((p) => p.id === projectId);
    setSubcontractorDraft((prev) => ({
      ...prev,
      projectId,
      clientId: proj?.client_id ? proj.client_id : prev.clientId,
    }));
  };

  // Subcontractor draft line operations (Weekly mode)
  const handleSubcontractorLineChange = (
    idx: number,
    field: 'work_type_id' | 'worker_count' | 'daily_rate' | 'remarks' | 'custom_work_name' | 'is_custom',
    value: any,
  ) => {
    setSubcontractorDraft((prev) => {
      const lines = [...prev.lines];
      const cur = lines[idx];
      if (!cur) return prev;

      const updated = {
        ...cur,
        [field]: value,
      };

      const rate = parseFloat(updated.daily_rate) || 0;
      updated.worker_count = '1';
      updated.totalAmountPaise = Math.round(rate * 100);
      lines[idx] = updated;
      return { ...prev, lines };
    });
  };

  const addSubcontractorLine = () => {
    setSubcontractorDraft((prev) => ({
      ...prev,
      lines: [
        ...prev.lines,
        {
          work_type_id: '',
          worker_count: '1',
          daily_rate: '',
          totalAmountPaise: 0,
          remarks: '',
          custom_work_name: '',
          is_custom: false,
        },
      ],
    }));
  };

  const removeSubcontractorLine = (idx: number) => {
    if (subcontractorDraft.lines.length <= 1) return;
    setSubcontractorDraft((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== idx),
    }));
  };

  // Individual work line operations
  const handleIndividualLineChange = (
    idx: number,
    field: 'nameOfWork' | 'workVolume' | 'unit' | 'rate',
    value: string,
  ) => {
    setSubcontractorDraft((prev) => {
      const currentLines =
        prev.individualLines && prev.individualLines.length > 0
          ? [...prev.individualLines]
          : [
              {
                nameOfWork: prev.nameOfWork || '',
                workVolume: prev.workVolume || '',
                unit: prev.unit || 'Sq.ft',
                rate: prev.rate || '',
              },
            ];
      const cur = currentLines[idx] || { nameOfWork: '', workVolume: '', unit: 'Sq.ft', rate: '' };
      currentLines[idx] = { ...cur, [field]: value };

      return {
        ...prev,
        individualLines: currentLines,
        ...(idx === 0
          ? {
              nameOfWork: currentLines[0]?.nameOfWork,
              workVolume: currentLines[0]?.workVolume,
              unit: currentLines[0]?.unit,
              rate: currentLines[0]?.rate,
            }
          : {}),
      };
    });
  };

  const addIndividualLine = () => {
    setSubcontractorDraft((prev) => {
      const currentLines =
        prev.individualLines && prev.individualLines.length > 0
          ? [...prev.individualLines]
          : [
              {
                nameOfWork: prev.nameOfWork || '',
                workVolume: prev.workVolume || '',
                unit: prev.unit || 'Sq.ft',
                rate: prev.rate || '',
              },
            ];
      return {
        ...prev,
        individualLines: [
          ...currentLines,
          {
            nameOfWork: '',
            workVolume: '',
            unit: 'Sq.ft',
            rate: '',
          },
        ],
      };
    });
  };

  const removeIndividualLine = (idx: number) => {
    setSubcontractorDraft((prev) => {
      const currentLines = prev.individualLines || [];
      if (currentLines.length <= 1) return prev;
      const updated = currentLines.filter((_, i) => i !== idx);
      return {
        ...prev,
        individualLines: updated,
        nameOfWork: updated[0]?.nameOfWork || '',
        workVolume: updated[0]?.workVolume || '',
        unit: updated[0]?.unit || 'Sq.ft',
        rate: updated[0]?.rate || '',
      };
    });
  };

  // Calculations
  const subcontractorOverallTotalRupees = useMemo(() => {
    const totalPaise = subcontractorDraft.lines.reduce((s, l) => s + l.totalAmountPaise, 0);
    return totalPaise / 100;
  }, [subcontractorDraft.lines]);

  const subcontractorPaidNum = parseFloat(subcontractorDraft.paidAmountRupees) || 0;

  const subcontractorBalanceSummaryQ = useSubcontractorBalanceSummary(
    subcontractorDraft.subcontractorId,
    subcontractorDraft.projectId,
  );
  const dbSubcontractorBalanceOwedPaise = subcontractorDraft.projectId
    ? (subcontractorBalanceSummaryQ.data?.projectBalanceOwedPaise ?? 0)
    : (subcontractorBalanceSummaryQ.data?.balanceOwedPaise ?? 0);
  const editingOriginalSubcontractorBatch = existing
    ? subcontractorBalanceSummaryQ.data?.batchMap?.[existing.id]
    : undefined;
  const editingOriginalSubcontractorPayment = existing
    ? subcontractorBalanceSummaryQ.data?.paymentMap?.[existing.id]
    : undefined;

  const priorSubcontractorBalancePaise = useMemo(() => {
    if (editingOriginalSubcontractorBatch) {
      return Math.max(
        0,
        dbSubcontractorBalanceOwedPaise -
          editingOriginalSubcontractorBatch.totalAmountPaise +
          editingOriginalSubcontractorBatch.paidAmountPaise,
      );
    }
    if (editingOriginalSubcontractorPayment) {
      return dbSubcontractorBalanceOwedPaise + editingOriginalSubcontractorPayment.amountPaise;
    }
    return dbSubcontractorBalanceOwedPaise;
  }, [
    dbSubcontractorBalanceOwedPaise,
    editingOriginalSubcontractorBatch,
    editingOriginalSubcontractorPayment,
  ]);

  const priorSubcontractorBalanceRupees = priorSubcontractorBalancePaise / 100;
  // (Previous Balance + This Work Credit) - Total Debit Paid
  const netSubcontractorBalanceToGiveRupees = (priorSubcontractorBalanceRupees + subcontractorOverallTotalRupees) - subcontractorPaidNum;
  const totalSubcontractorBalanceToGiveRupees = Math.max(0, netSubcontractorBalanceToGiveRupees);
  const excessSubcontractorAdvanceRupees = Math.max(0, -netSubcontractorBalanceToGiveRupees);

  const currentSubcontractorContract = useMemo(() => {
    if (!subcontractorDraft.subcontractorId || !subcontractorDraft.projectId) return null;
    return (
      allSubcontractorContracts?.find(
        (c) =>
          c.subcontractorId === subcontractorDraft.subcontractorId &&
          c.projectId === subcontractorDraft.projectId,
      ) ?? null
    );
  }, [allSubcontractorContracts, subcontractorDraft.subcontractorId, subcontractorDraft.projectId]);

  const individualTotalRupees = useMemo(() => {
    if (subcontractorDraft.individualLines && subcontractorDraft.individualLines.length > 0) {
      return subcontractorDraft.individualLines.reduce((sum, item) => {
        const vol = parseFloat(item.workVolume || '0') || 0;
        const rate = parseFloat(item.rate || '0') || 0;
        return sum + vol * rate;
      }, 0);
    }
    const individualVolumeNum = parseFloat(subcontractorDraft.workVolume || '0') || 0;
    const individualRateNum = parseFloat(subcontractorDraft.rate || '0') || 0;
    return individualVolumeNum * individualRateNum;
  }, [subcontractorDraft.individualLines, subcontractorDraft.workVolume, subcontractorDraft.rate]);
  const indPaidNum = parseFloat(subcontractorDraft.paidAmountRupees || '0') || 0;
  const indBalanceNum = Math.max(0, individualTotalRupees - indPaidNum);

  // Submit Handler
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (!subcontractorDraft.projectId) throw new Error('Please select a project');
      if (!subcontractorDraft.subcontractorId) throw new Error('Please select a sub contractor');

      if (subcontractorDraft.entryMode === 'individual') {
        const lines =
          subcontractorDraft.individualLines && subcontractorDraft.individualLines.length > 0
            ? subcontractorDraft.individualLines
            : [
                {
                  nameOfWork: subcontractorDraft.nameOfWork || '',
                  workVolume: subcontractorDraft.workVolume || '',
                  unit: subcontractorDraft.unit || 'Sq.ft',
                  rate: subcontractorDraft.rate || '',
                },
              ];

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (!line || !line.nameOfWork?.trim()) {
            throw new Error(`Please enter the name of work for item #${i + 1}`);
          }
          const volume = parseFloat(line.workVolume || '0') || 0;
          if (volume <= 0) throw new Error(`Please enter a valid work volume for item #${i + 1}`);
          const rate = parseFloat(line.rate || '0') || 0;
          if (rate <= 0) throw new Error(`Please enter a valid rate per unit for item #${i + 1}`);
        }
      } else {
        // Weekly mode
        const hasLabourLines = subcontractorDraft.lines.some(
          (l) => (l.work_type_id || (l.is_custom && l.custom_work_name?.trim())) && parseFloat(l.daily_rate || '0') > 0,
        );
        const hasDebit = parseFloat(subcontractorDraft.paidAmountRupees || '0') > 0;
        if (!hasLabourLines && !hasDebit) {
          throw new Error('Please enter work name and weekly rate (Credit) or a paid amount (Debit)');
        }
      }

      const hasCredit =
        subcontractorDraft.entryMode === 'individual'
          ? individualTotalRupees > 0
          : subcontractorDraft.lines.some(
              (l) => (l.work_type_id || (l.is_custom && l.custom_work_name?.trim())) && parseFloat(l.daily_rate || '0') > 0,
            );

      const draftToSubmit: SubcontractorLabourDraft = {
        ...subcontractorDraft,
        txType: hasCredit ? 'credit' : 'debit',
        remarks: cleanDisplayRemarks(subcontractorDraft.remarks) || '',
      };

      if (existing) {
        await updateSubcontractorLabour.mutateAsync({ id: existing.id, draft: draftToSubmit });
        toast.show(
          subcontractorDraft.entryMode === 'individual'
            ? 'Individual work entry updated successfully'
            : draftToSubmit.txType === 'debit'
            ? 'Sub contractor payment (Debit) updated successfully'
            : 'Sub contractor labour entry updated successfully',
        );
      } else {
        await recordSubcontractorLabour.mutateAsync(draftToSubmit);
        toast.show(
          subcontractorDraft.entryMode === 'individual'
            ? 'Individual work entry recorded successfully'
            : draftToSubmit.txType === 'debit'
            ? 'Sub contractor payment (Debit) recorded successfully'
            : 'Sub contractor labour entry recorded successfully',
        );
      }

      void queryClient.invalidateQueries({ queryKey: ['subcontractor-transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['project'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['all-projects-report-data'] });
      void queryClient.invalidateQueries({ queryKey: ['daily-report'] });

      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.show(err.message || 'Could not save subcontractor entry', true);
    }
  };

  return (
    <Drawer
      title={
        existing
          ? subcontractorDraft.entryMode === 'individual'
            ? 'Edit Individual Work Entry'
            : 'Edit Weekly Subcontractor Entry'
          : subcontractorDraft.entryMode === 'individual'
          ? 'Record Individual Work & Payment'
          : 'Record Subcontractor Labour & Payment'
      }
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={
        existing
          ? 'Save Changes'
          : subcontractorDraft.entryMode === 'individual'
          ? 'Save Individual Entry'
          : 'Save Labour Entry & Payment'
      }
      busy={isSaving}
      style={{ width: 'min(860px, 95vw)' }}
    >
      {/* Mode Selector: Weekly Payment (Default) vs Individual Payment */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: 4,
          background: '#f1f5f9',
          borderRadius: 10,
          marginBottom: 16,
        }}
      >
        <button
          type="button"
          onClick={() => {
            if ((subcontractorDraft.entryMode || 'weekly') !== 'weekly') {
              setSubcontractorDraft(createInitialSubcontractorDraft('weekly'));
            }
          }}
          style={{
            flex: 1,
            padding: '9px 16px',
            borderRadius: 8,
            border: 'none',
            outline: 'none',
            background: (subcontractorDraft.entryMode || 'weekly') === 'weekly' ? '#ffffff' : 'transparent',
            color: (subcontractorDraft.entryMode || 'weekly') === 'weekly' ? '#1d4ed8' : '#64748b',
            fontWeight: (subcontractorDraft.entryMode || 'weekly') === 'weekly' ? 700 : 600,
            fontSize: 13.5,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            boxShadow: (subcontractorDraft.entryMode || 'weekly') === 'weekly' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s ease',
          }}
        >
          <Calendar size={16} strokeWidth={2.2} />
          <span>Weekly Payment</span>
        </button>

        <button
          type="button"
          onClick={() => {
            if (subcontractorDraft.entryMode !== 'individual') {
              setSubcontractorDraft(createInitialSubcontractorDraft('individual'));
            }
          }}
          style={{
            flex: 1,
            padding: '9px 16px',
            borderRadius: 8,
            border: 'none',
            outline: 'none',
            background: subcontractorDraft.entryMode === 'individual' ? '#ffffff' : 'transparent',
            color: subcontractorDraft.entryMode === 'individual' ? '#0e7490' : '#64748b',
            fontWeight: subcontractorDraft.entryMode === 'individual' ? 700 : 600,
            fontSize: 13.5,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            boxShadow: subcontractorDraft.entryMode === 'individual' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s ease',
          }}
        >
          <Layers size={16} strokeWidth={2.2} />
          <span>Individual Payment</span>
        </button>
      </div>

      {/* Header Info: One by one, one field per row */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 }}>
        <Field label="Date">
          <input
            type="date"
            value={subcontractorDraft.date}
            onChange={(e) => setSubcontractorDraft((prev) => ({ ...prev, date: e.target.value }))}
            required
          />
        </Field>

        <Field label="Select Client">
          <Select
            value={subcontractorDraft.clientId || ''}
            onChange={handleSubcontractorClientChange}
            options={lookups?.clients?.map((c) => ({ value: c.id, label: c.name })) ?? []}
          />
        </Field>

        <Field label="Select Project">
          <Select
            value={subcontractorDraft.projectId}
            onChange={handleSubcontractorProjectChange}
            options={subcontractorProjects.map((p) => ({ value: p.id, label: p.name }))}
          />
        </Field>

        <Field label="Select Sub contractor">
          <Select
            value={subcontractorDraft.subcontractorId}
            onChange={(v) => {
              if (v === '__new_subcontractor__') {
                setShowQuickAddSub(true);
                return;
              }
              setSubcontractorDraft((prev) => ({ ...prev, subcontractorId: v }));
            }}
            options={[
              ...(subcontractorDraft.entryMode === 'individual'
                ? [{ value: '__new_subcontractor__', label: '+ Add New Sub Contractor' }]
                : []),
              ...(lookups?.subcontractors?.map((s) => ({ value: s.id, label: s.name })) ?? []),
            ]}
          />
        </Field>

        {/* Quick Add Subcontractor Form (Individual mode alone) */}
        {subcontractorDraft.entryMode === 'individual' && showQuickAddSub && (
          <div
            style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
              border: '1.5px solid #93c5fd',
              borderRadius: 10,
              padding: '16px 18px',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.08)',
              animation: 'fadeIn 0.15s ease-out',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 13.5, color: '#1e40af' }}>
                <HardHat size={16} />
                <span>Add New Sub Contractor</span>
              </div>
              <button
                type="button"
                onClick={() => setShowQuickAddSub(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: 16,
                  fontWeight: 700,
                  padding: '0 4px',
                }}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Field label="Sub Contractor Name">
                <input
                  value={newSubName}
                  onChange={(e) => setNewSubName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleCreateQuickSub();
                    }
                  }}
                  autoFocus
                  required
                />
              </Field>

              <Field label="Sub Contractor Phone Number">
                <input
                  type="tel"
                  value={newSubPhone}
                  onChange={(e) => setNewSubPhone(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleCreateQuickSub();
                    }
                  }}
                  required
                />
              </Field>

              <Field label="Location">
                <input
                  value={newSubLocation}
                  onChange={(e) => setNewSubLocation(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleCreateQuickSub();
                    }
                  }}
                />
              </Field>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                <button
                  type="button"
                  onClick={() => setShowQuickAddSub(false)}
                  style={{
                    padding: '8px 16px',
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#475569',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleCreateQuickSub()}
                  disabled={isCreatingSub || !newSubName.trim() || !newSubPhone.trim()}
                  style={{
                    padding: '8px 18px',
                    background: '#2563eb',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#ffffff',
                    cursor: isCreatingSub || !newSubName.trim() || !newSubPhone.trim() ? 'not-allowed' : 'pointer',
                    opacity: isCreatingSub || !newSubName.trim() || !newSubPhone.trim() ? 0.6 : 1,
                    boxShadow: '0 1px 2px rgba(37, 99, 235, 0.2)',
                  }}
                >
                  {isCreatingSub ? 'Saving...' : 'Save Sub Contractor'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* =========================================================================
          A. WEEKLY PAYMENT WORKFLOW
          ========================================================================= */}
      {(subcontractorDraft.entryMode || 'weekly') === 'weekly' && (
        <>
          {/* Subcontractor Contract & Weekly Balance Summary (Project Specific, Weekly mode only) */}
          {(currentSubcontractorContract || (subcontractorDraft.projectId && priorSubcontractorBalanceRupees > 0)) && (
            <div
              style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
                border: '1px solid #bfdbfe',
                borderRadius: 8,
                padding: '10px 12px',
                marginBottom: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <HardHat size={15} color="#2563eb" />
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Sub Contractor Project Contract
                  </span>
                </div>
                {currentSubcontractorContract && (
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#dbeafe', color: '#1e40af', textTransform: 'uppercase' }}>
                    {currentSubcontractorContract.status || 'Ongoing'}
                  </span>
                )}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    currentSubcontractorContract && priorSubcontractorBalanceRupees > 0
                      ? 'repeat(auto-fit, minmax(min(100%, 120px), 1fr))'
                      : currentSubcontractorContract
                      ? 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))'
                      : '1fr',
                  gap: 10,
                }}
              >
                {currentSubcontractorContract && (
                  <>
                    <div style={{ background: '#ffffff', padding: '8px 10px', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 10.5, fontWeight: 600, color: '#64748b' }}>Total Amount</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                        {formatMoney(currentSubcontractorContract.contractAmountPaise)}
                      </div>
                    </div>
                    <div style={{ background: '#ffffff', padding: '8px 10px', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 10.5, fontWeight: 600, color: '#64748b' }}>Need to Pay</div>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 800,
                          color: currentSubcontractorContract.pendingAmountPaise > 0 ? '#ea580c' : '#15803d',
                          fontFamily: 'var(--font-mono)',
                          marginTop: 2,
                        }}
                      >
                        {formatMoney(currentSubcontractorContract.pendingAmountPaise)}
                      </div>
                    </div>
                  </>
                )}
                {priorSubcontractorBalanceRupees > 0 && (
                  <div style={{ background: '#fffbeb', padding: '8px 10px', borderRadius: 6, border: '1px solid #fde68a' }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: '#92400e' }}>Weekly Balance</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#b45309', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                      ₹{priorSubcontractorBalanceRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Work Types & Weekly Rate Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <HardHat size={18} strokeWidth={2.2} style={{ color: '#0f172a' }} />
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
              Work Types & Weekly Rate
            </h4>
          </div>

          {/* Work Types Card Container */}
          <div
            style={{
              background: '#ffffff',
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              padding: '16px 18px',
              marginBottom: 18,
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            }}
          >
            {/* Column Headers: WORK NAME and WEEKLY RATE (₹) (Desktop Only) */}
            <div className="subcontractor-table-header">
              <div>WORK NAME</div>
              <div>WEEKLY RATE (₹)</div>
              <div />
            </div>

            {/* Line Items */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 10 }}>
              {subcontractorDraft.lines.map((line, idx) => (
                <div key={idx} className="subcontractor-line-item">
                  {/* 1. Work Name Selector + Remove Button on mobile */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <label className="subcontractor-line-mobile-label">
                        Work Name
                      </label>
                      {line.is_custom ? (
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <input
                            type="text"
                            autoFocus
                            value={line.custom_work_name || ''}
                            onChange={(e) => handleSubcontractorLineChange(idx, 'custom_work_name', e.target.value)}
                            style={{
                              width: '100%',
                              height: 38,
                              background: '#ffffff',
                              border: '1px solid #3b82f6',
                              borderRadius: 6,
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#0f172a',
                              padding: '0 30px 0 10px',
                              boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.15)',
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              handleSubcontractorLineChange(idx, 'is_custom', false);
                              handleSubcontractorLineChange(idx, 'work_type_id', '');
                              handleSubcontractorLineChange(idx, 'custom_work_name', '');
                            }}
                            style={{
                              position: 'absolute',
                              right: 6,
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#94a3b8',
                              padding: 4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            title="Switch back to dropdown"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <Select
                          size="sm"
                          value={line.work_type_id}
                          options={subcontractorWorkTypeOptions}
                          onChange={(val) => {
                            const selectedOpt = subcontractorWorkTypeOptions.find((o) => o.value === val);
                            const isOthers = val === '__other__' || selectedOpt?.label?.trim().toLowerCase() === 'others';
                            if (isOthers) {
                              handleSubcontractorLineChange(idx, 'is_custom', true);
                              handleSubcontractorLineChange(idx, 'work_type_id', val);
                              handleSubcontractorLineChange(idx, 'custom_work_name', '');
                            } else {
                              handleSubcontractorLineChange(idx, 'work_type_id', val);
                            }
                          }}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSubcontractorLine(idx)}
                      disabled={subcontractorDraft.lines.length <= 1}
                      className="subcontractor-remove-btn-mobile"
                      title="Remove line"
                    >
                      <Trash2 size={16} strokeWidth={1.8} />
                    </button>
                  </div>

                  {/* 2. Weekly Rate */}
                  <div style={{ width: '100%' }}>
                    <label className="subcontractor-line-mobile-label">
                      Weekly Rate (₹)
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={line.daily_rate}
                      onChange={(e) => handleSubcontractorLineChange(idx, 'daily_rate', e.target.value)}
                      style={{
                        width: '100%',
                        height: 38,
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: 'var(--font-mono)',
                        color: '#0f172a',
                        padding: '0 10px',
                      }}
                    />
                  </div>

                  {/* 3. Remove Line Button (Desktop Only) */}
                  <div className="subcontractor-remove-btn-desktop">
                    <button
                      type="button"
                      onClick={() => removeSubcontractorLine(idx)}
                      disabled={subcontractorDraft.lines.length <= 1}
                      style={{
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'none',
                        border: 'none',
                        cursor: subcontractorDraft.lines.length <= 1 ? 'not-allowed' : 'pointer',
                        color: '#94a3b8',
                        borderRadius: 4,
                        opacity: subcontractorDraft.lines.length <= 1 ? 0.3 : 1,
                        transition: 'color 0.15s ease',
                      }}
                      title="Remove line"
                    >
                      <Trash2 size={16} strokeWidth={1.8} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Another Work Type Button */}
            <div style={{ marginTop: 14 }}>
              <button
                type="button"
                onClick={addSubcontractorLine}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  color: '#2563eb',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <Plus size={15} strokeWidth={2.4} />
                <span>Add Another Work Type</span>
              </button>
            </div>
          </div>

          {/* Debit & Credit Section */}
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '12px 14px',
              marginBottom: 14,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
              Debit & Credit
            </div>

            <div className="grid3" style={{ gap: 10 }}>
              <Field label="Credit (₹)">
                <div
                  style={{
                    height: 34,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 10px',
                    borderRadius: 6,
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: 'var(--font-mono)',
                    color: '#0f172a',
                    width: '100%',
                  }}
                >
                  ₹{subcontractorOverallTotalRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </Field>

              <Field label="Debit (₹)" optional>
                <input
                  type="number"
                  step="any"
                  value={subcontractorDraft.paidAmountRupees}
                  onChange={(e) => setSubcontractorDraft((prev) => ({ ...prev, paidAmountRupees: e.target.value }))}
                  style={{
                    width: '100%',
                    height: 34,
                    fontWeight: 600,
                    fontFamily: 'var(--font-mono)',
                    color: '#059669',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    padding: '0 10px',
                    background: '#ffffff',
                    fontSize: 13,
                  }}
                />
              </Field>

              <Field label="Total Balance (₹)">
                <div
                  style={{
                    height: 34,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 10px',
                    borderRadius: 6,
                    border: totalSubcontractorBalanceToGiveRupees === 0 ? '1px solid #bbf7d0' : '1px solid #fecaca',
                    background: totalSubcontractorBalanceToGiveRupees === 0 ? '#f0fdf4' : '#fef2f2',
                    color: totalSubcontractorBalanceToGiveRupees === 0 ? '#15803d' : '#dc2626',
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: 'var(--font-mono)',
                    width: '100%',
                  }}
                >
                  ₹{totalSubcontractorBalanceToGiveRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </Field>
            </div>

            {/* Total Balance (Previous Balance + This Bill) Banner */}
            <div
              style={{
                marginTop: 10,
                padding: '8px 12px',
                borderRadius: 6,
                background: totalSubcontractorBalanceToGiveRupees > 0 ? '#eff6ff' : '#f0fdf4',
                border: totalSubcontractorBalanceToGiveRupees > 0 ? '1px solid #bfdbfe' : '1px solid #bbf7d0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, color: totalSubcontractorBalanceToGiveRupees > 0 ? '#1e40af' : '#166534', letterSpacing: '0.02em' }}>
                {netSubcontractorBalanceToGiveRupees < 0
                  ? 'ADVANCE / SURPLUS TO SUB CONTRACTOR'
                  : 'TOTAL BALANCE (PREVIOUS BALANCE + THIS BILL)'}
              </div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 800,
                  fontFamily: 'var(--font-mono)',
                  color: totalSubcontractorBalanceToGiveRupees > 0 ? '#1d4ed8' : '#15803d',
                  whiteSpace: 'nowrap',
                }}
              >
                {netSubcontractorBalanceToGiveRupees < 0
                  ? `₹${excessSubcontractorAdvanceRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Advance)`
                  : `₹${totalSubcontractorBalanceToGiveRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </div>
            </div>
          </div>

          {/* Mode & Remarks */}
          <div className="grid2" style={{ gap: 14 }}>
            <Field label="Mode of Payment">
              <Select
                value={subcontractorDraft.mode}
                onChange={(v) => setSubcontractorDraft((prev) => ({ ...prev, mode: v }))}
                options={PAYMENT_MODES.map((m) => ({ value: m.label, label: m.label }))}
              />
            </Field>

            <Field label="Remarks" optional>
              <input
                value={subcontractorDraft.remarks}
                onChange={(e) => setSubcontractorDraft((prev) => ({ ...prev, remarks: e.target.value }))}
              />
            </Field>
          </div>
        </>
      )}

      {/* =========================================================================
          B. INDIVIDUAL PAYMENT WORKFLOW
          ========================================================================= */}
      {subcontractorDraft.entryMode === 'individual' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Multiple Individual Work Lines: 1 row 4 column order, responsive */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(subcontractorDraft.individualLines && subcontractorDraft.individualLines.length > 0
              ? subcontractorDraft.individualLines
              : [
                  {
                    nameOfWork: subcontractorDraft.nameOfWork || '',
                    workVolume: subcontractorDraft.workVolume || '',
                    unit: subcontractorDraft.unit || 'Sq.ft',
                    rate: subcontractorDraft.rate || '',
                  },
                ]
            ).map((item, idx) => {
              const hasMultiple = (subcontractorDraft.individualLines?.length ?? 1) > 1;
              return (
                <div
                  key={idx}
                  className={`individual-work-item ${!hasMultiple ? 'no-delete' : ''}`}
                >
                  {/* Column 1: Name of Work */}
                  <div className="work-col-field work-col-name">
                    <Field label="Name of Work">
                      <input
                        value={item.nameOfWork || ''}
                        onChange={(e) => handleIndividualLineChange(idx, 'nameOfWork', e.target.value)}
                        required
                      />
                    </Field>
                  </div>

                  {/* Column 2: Work Volume / Quantity */}
                  <div className="work-col-field work-col-volume">
                    <Field label="Work Volume / Quantity">
                      <input
                        type="number"
                        step="any"
                        min="0.01"
                        value={item.workVolume || ''}
                        onChange={(e) => handleIndividualLineChange(idx, 'workVolume', e.target.value)}
                        style={{
                          fontWeight: 700,
                          fontFamily: 'var(--font-mono)',
                          fontSize: 14,
                        }}
                        required
                      />
                    </Field>
                  </div>

                  {/* Column 3: Unit */}
                  <div className="work-col-field work-col-unit">
                    <Field label="Unit">
                      <Select
                        value={item.unit || 'Sq.ft'}
                        onChange={(v) => handleIndividualLineChange(idx, 'unit', v)}
                        options={INDIVIDUAL_UNITS}
                      />
                    </Field>
                  </div>

                  {/* Column 4: Rate per Unit (₹) */}
                  <div className="work-col-field work-col-rate">
                    <Field label="Rate per Unit (₹)">
                      <input
                        type="number"
                        step="any"
                        min="0.01"
                        value={item.rate || ''}
                        onChange={(e) => handleIndividualLineChange(idx, 'rate', e.target.value)}
                        style={{
                          fontWeight: 700,
                          fontFamily: 'var(--font-mono)',
                          fontSize: 14,
                        }}
                        required
                      />
                    </Field>
                  </div>

                  {/* Action Button: Remove Line if multiple */}
                  {hasMultiple && (
                    <div className="work-col-action">
                      <button
                        type="button"
                        onClick={() => removeIndividualLine(idx)}
                        style={{
                          width: 38,
                          height: 38,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'none',
                          border: '1px solid #e2e8f0',
                          borderRadius: 6,
                          cursor: 'pointer',
                          color: '#94a3b8',
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#ef4444';
                          e.currentTarget.style.borderColor = '#fca5a5';
                          e.currentTarget.style.background = '#fef2f2';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#94a3b8';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.background = 'none';
                        }}
                        title="Remove worker detail"
                      >
                        <Trash2 size={16} strokeWidth={1.8} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add Another Worker Detail Button */}
          <div style={{ marginTop: 2, marginBottom: 4 }}>
            <button
              type="button"
              onClick={addIndividualLine}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                color: '#2563eb',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#dbeafe';
                e.currentTarget.style.borderColor = '#93c5fd';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#eff6ff';
                e.currentTarget.style.borderColor = '#bfdbfe';
              }}
            >
              <Plus size={15} strokeWidth={2.4} />
              <span>Add Another Worker Detail</span>
            </button>
          </div>

          {/* Debit & Credit Section */}
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '12px 14px',
              marginBottom: 14,
              marginTop: 12,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
              Debit & Credit
            </div>

            <div className="grid3" style={{ gap: 10 }}>
              <Field label="Credit (₹)">
                <div
                  style={{
                    height: 34,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 10px',
                    borderRadius: 6,
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: 'var(--font-mono)',
                    color: '#0f172a',
                    width: '100%',
                  }}
                >
                  ₹{individualTotalRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </Field>

              <Field label="Debit (₹)" optional>
                <input
                  type="number"
                  step="any"
                  value={subcontractorDraft.paidAmountRupees}
                  onChange={(e) => setSubcontractorDraft((prev) => ({ ...prev, paidAmountRupees: e.target.value }))}
                  style={{
                    width: '100%',
                    height: 34,
                    fontWeight: 600,
                    fontFamily: 'var(--font-mono)',
                    color: '#059669',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    padding: '0 10px',
                    background: '#ffffff',
                    fontSize: 13,
                  }}
                />
              </Field>

              <Field label="This Bill Balance (₹)">
                <div
                  style={{
                    height: 34,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 10px',
                    borderRadius: 6,
                    border: indBalanceNum === 0 ? '1px solid #bbf7d0' : '1px solid #fecaca',
                    background: indBalanceNum === 0 ? '#f0fdf4' : '#fef2f2',
                    color: indBalanceNum === 0 ? '#15803d' : '#dc2626',
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: 'var(--font-mono)',
                    width: '100%',
                  }}
                >
                  ₹{indBalanceNum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </Field>
            </div>
          </div>

          {/* Mode of Payment & Remarks */}
          <div className="grid2" style={{ gap: 14 }}>
            <Field label="Mode of Payment">
              <Select
                value={subcontractorDraft.mode}
                onChange={(v) => setSubcontractorDraft((prev) => ({ ...prev, mode: v }))}
                options={PAYMENT_MODES.map((m) => ({ value: m.label, label: m.label }))}
              />
            </Field>

            <Field label="Remarks" optional>
              <input
                value={subcontractorDraft.remarks}
                onChange={(e) => setSubcontractorDraft((prev) => ({ ...prev, remarks: e.target.value }))}
              />
            </Field>
          </div>
        </div>
      )}
    </Drawer>
  );
}
